import { FormControl, FormControlProps, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import type { JSX } from "react";
import { ReactNode } from "react";
import { Controller } from "react-hook-form";
import type { Control, FieldPath, FieldValues, PathValue } from "react-hook-form";

export interface ToggleOption<T extends string | number | boolean> {
  value: T;
  label: string | ReactNode;
  disabled?: boolean;
}

export interface FormControlToggleButtonGroupProps<
  T extends string | number | boolean,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = unknown,
> {
  name: TName;
  control: Control<TFieldValues, TContext>;
  label?: string;
  errorMsg?: string;
  options: ToggleOption<T>[];
  mapChangedValue?: (value: T) => PathValue<TFieldValues, TName>;
  mapValue?: (value: PathValue<TFieldValues, TName>) => T | null;
  sx?: FormControlProps["sx"];
  fullWidth?: boolean;
  "data-cy"?: string;
}

export const FormControlToggleButtonGroup = <
  T extends string | number | boolean,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = unknown,
>({
  name,
  control,
  label,
  errorMsg,
  options,
  mapChangedValue,
  sx,
  fullWidth,
  "data-cy": dataCy,
}: FormControlToggleButtonGroupProps<T, TFieldValues, TName, TContext>): JSX.Element => (
  <FormControl error={Boolean(errorMsg)} fullWidth={fullWidth}>
    {label && <Typography sx={{ mb: 1, fontSize: "16px", fontWeight: 600 }}>{label}</Typography>}
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        return (
          <ToggleButtonGroup
            exclusive
            value={value ?? ""}
            onChange={(_, newValue) => {
              if (newValue !== null) {
                onChange(mapChangedValue?.(newValue as T) ?? newValue);
              }
            }}
            aria-label={label}
            sx={{
              ...sx,
              width: "100%",
              "& .MuiToggleButtonGroup-middleButton, & .MuiToggleButtonGroup-lastButton": {
                margin: 0,
              },
              ...(errorMsg
                ? {
                    "& .MuiToggleButton-root": {
                      border: "1px solid #d32f2f",
                    },
                  }
                : {}),
            }}
            data-cy={dataCy}
          >
            {options.map((option) => (
              <ToggleButton key={option.value.toString()} value={option.value} disabled={option.disabled}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        );
      }}
    />
    {errorMsg && (
      <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.75 }}>
        {errorMsg}
      </Typography>
    )}
  </FormControl>
);
